import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ResolvedTheme, ThemePreference } from 'theme/types';
import {
  applyResolvedTheme,
  applyThemeToDocument,
  readThemePreference,
  resolveTheme,
  subscribeToSystemTheme,
  subscribeToThemePreferenceChanges,
  writeThemePreference,
} from 'theme/themePreference';

export interface ThemeContextValue {
  /** Persisted user choice: light, dark, or follow system. */
  preference: ThemePreference;
  /** Effective theme after resolving system preference. */
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readThemePreference(),
  );
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(readThemePreference()),
  );

  useLayoutEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (preference !== 'system') {
      return;
    }
    return subscribeToSystemTheme(setResolvedTheme);
  }, [preference]);

  useEffect(() => {
    return subscribeToThemePreferenceChanges((next) => {
      const resolved = applyThemeToDocument(next);
      setPreferenceState(next);
      setResolvedTheme(resolved);
    });
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    writeThemePreference(next);
    setPreferenceState(next);
    setResolvedTheme(resolveTheme(next));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolvedTheme,
      setPreference,
    }),
    [preference, resolvedTheme, setPreference],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
